import React, {useEffect, useRef, useState} from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import CustomSortableItem from './CustomSortableItem';
import {calculateHeight} from "../Apis/functions";
/**
 *
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTitle: Frontend
 * @Description: A reusable vertical sortable list component using @dnd-kit.
 *              Allows items to be reordered via drag-and-drop and notifies when the order changes.
 * @param {Array<Object>} items - Array of objects representing the sortable items. Each item must have a unique identifier.
 * @param {Function} setItems - Function to update the items state when their order changes.
 * @param {Function} renderItem - Function that returns the JSX element for each item. Receives `(item, index, isDragging)` as parameters.
 * @param {Function} [onSortEnd] - Optional callback executed when the sorting ends. Receives an object with `id`, `oldIndex`, `newIndex`, and `newItems`.
 * @param {string} [keyId="id"] - The key name used to uniquely identify each item in the list.
 * @param {string} [className="overflow-y-auto"] - Optional classname applied to the container wrapper.
 *
 * @param sortKey - Optional parameter to soft the list
 * @param className - Optional class for the container
 * @param dynamicHeight - Optional boolean if is true calculate height if this argument is false not
 * @returns {JSX.Element} The rendered sortable list component.
 *
 * />
 */
const CustomSortableList = ({
                                items,
                                setItems,
                                renderItem,
                                onSortEnd,
                                keyId = "id",
                                sortKey,
                                className = "overflow-y-auto",
                                dynamicHeight= true
                            }) => {

    const sensors = useSensors(useSensor(PointerSensor));
    const elementRef = useRef(null);
    const [isDragging, setIsDragging] = useState(null);

    /**
     * Calculates and sets the container height dynamically.
     *
     * @function useEffect:calculateHeight
     */
    useEffect(() => {
        if (dynamicHeight) {
            calculateHeight({ elementRef: elementRef, bottom: 80 });
        }
    }, []);

    /**
     * Memoized sorted list based on the provided `sortKey`.
     *
     * @function sortedItems
     * @returns {Array<Object>} The sorted array of items.
     */
    const sortedItems = React.useMemo(() => {
        if (!sortKey) return items;

        return [...items].sort((a, b) => {
            const aVal = a?.[sortKey];
            const bVal = b?.[sortKey];

            if (typeof aVal === "number" && typeof bVal === "number") {
                return aVal - bVal;
            }

            return String(aVal).localeCompare(String(bVal));
        });

    }, [items, sortKey]);

    /**
     * Ensures only valid items with a defined unique key are included.
     *
     * @constant validItems
     * @type {Array<Object>}
     */
    const validItems = sortedItems.filter(item => item && item[keyId] != null);

    /**
     * Handles the end of a drag-and-drop sort operation.
     *
     * @function handleDragEnd
     * @param {Object} event - Drag event from dnd-kit.
     * @param {Object} event.active - The item being dragged.
     * @param {Object} event.over - The item currently being hovered over.
     */
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setIsDragging(null);

        if (active.id !== over.id) {
            const oldIndex = items.findIndex(item => item[keyId] === active.id);
            const newIndex = items.findIndex(item => item[keyId] === over.id);

            const newItems = arrayMove(items, oldIndex, newIndex);
            setItems(newItems);

            if (onSortEnd) {
                onSortEnd({
                    id: active.id,
                    oldIndex,
                    newIndex,
                    newItems
                });
            }
        }
    };

    return (
        <div ref={elementRef} className={className}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}
                        onDragStart={(e) => setIsDragging(e.active.id)}
            >
                <SortableContext
                    items={validItems.map(i => i[keyId])}
                    strategy={verticalListSortingStrategy}
                >
                    {sortedItems.map((item, index) => (
                        <CustomSortableItem key={item[keyId]} id={item[keyId]}>
                            {renderItem(item, index, isDragging === item[keyId])}
                        </CustomSortableItem>
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default CustomSortableList;
